import { addressArrayLike, addressLike } from "@gearbox-protocol/cli-utils";
import { NetworkType } from "@gearbox-protocol/sdk/onchain";
import { z } from "zod";
export const scriptTemplateSchema = z.object({
    id: z.string().regex(/^[0-9a-zA-Z\-_]+$/, "invalid script id"),
    description: z
        .string()
        .nullish()
        .transform(s => s || undefined)
        .optional(),
    npx: z.string().optional(),
    docker: z.string().optional(),
    timeout: z.string().optional(),
    args: z
        .array(z.string().min(1))
        .nullish()
        .transform(v => (v?.length ? v : undefined)), // replace empty array with undefined
    env: z
        .record(z.string(), z.string())
        .nullish()
        .transform(v => (Object.keys(v ?? {}).length ? v : undefined)), // replace empty object with undefined
    flags: z
        .array(z.string().min(1))
        .nullish()
        .transform(v => (v?.length ? v : undefined))
        .optional(), // replace array object with undefined
    input: z.any().optional(),
});
export const backendOptionsSchema = z.object({
    // Optional image version/tag override
    version: z.string().optional(),
    // Extra env passed to the backend container, interpolated like ScriptOpts.Env
    env: z.record(z.string(), z.string()).optional(),
    // Neon branch_id to use as parent for the provisioned database branch
    parentBranchId: z.string().optional(),
});
export const forkTemplateSchema = z.object({
    alias: z
        .string()
        .regex(/^[0-9a-zA-Z\-_]{3,}$/, {
        message: "alias must contain letters, numbers, dashes and underscores, and must contain minimum 3 symbols",
    })
        .or(z.literal(""))
        .nullish(),
    description: z
        .string()
        .nullish()
        .transform(s => s || undefined),
    /**
     * Anvil chain id override, if not set, defaults to same as original network chain id (e.g. 1 for mainnet)
     */
    chainId: z
        .number()
        .int()
        .nonnegative()
        .nullish()
        .transform(v => v || undefined)
        .optional(),
    /**
     * Optional address provider pverride
     */
    addressProvider: z.nullable(addressLike()).optional(),
    /**
     * Optional market configurators override
     */
    marketConfigurators: z.nullable(addressArrayLike()).optional(),
    /**
     * Optional RWA factories override
     */
    rwaFactories: z.nullable(addressArrayLike()).optional(),
    /**
     * Will set appropriate RPC urls
     */
    network: NetworkType.default("Mainnet"),
    /**
     * Extra flags to pass to anvil
     */
    anvilFlags: z
        .array(z.string())
        .nullish()
        .transform(v => (v?.length ? v : undefined))
        .optional(),
    /**
     * Docker image tag or full version to override foundry image
     */
    anvilImage: z.string().optional(),
    /**
     * Block offset expression to determine starting block of a fork
     * Expression has following rules:
     * <empty string> = latest block
     * 12244555 = absolute block number
     * -7200 = latest block -7200 blocks
     * -1h30m = latest block -1 hour 30 minutes
     * @ = previous Monday 12:00 UTC
     * @2h = previous Monday 12:00 UTC + 2 hours
     * @-7200 = previous Monday 12:00 UTC -7200 blocks
     */
    blockNumber: z
        .string()
        .nullish()
        .transform(v => v || undefined)
        .optional(),
    scripts: z
        .array(scriptTemplateSchema)
        .nullish()
        .refine(scripts => {
        const ids = scripts?.map(s => s.id) ?? [];
        return ids.length === new Set(ids).size;
    }, {
        message: "scripts must have unique ids",
    }),
    timeout: z.string().default("3h"),
    debug: z.boolean().optional(),
    /**
     * It's possible to make particular testnet's synctron verbose
     */
    synctronLogLevel: z.literal("debug").optional(),
    disableRPCProtection: z
        .boolean()
        .optional()
        .transform(v => (v ? v : undefined))
        .optional(),
    /**
     * RPC provider override
     */
    rpcProvider: z
        .string()
        .optional()
        .transform(v => (v ? v : undefined))
        .optional(),
    disableSync: z
        .boolean()
        .optional()
        .transform(v => (v ? v : undefined))
        .optional(),
    backend: backendOptionsSchema.optional(),
});
